/**
 * Theme Configuration System
 * 
 * Controls the visual identity of the catalog from a single point.
 * Usage: Set REACT_APP_THEME in .env to a theme name, or use
 * individual REACT_APP_COLOR_* overrides for fine-tuning.
 */

// ===== Predefined Themes =====
const themes = {
    surtifamiliar: {
        colorBg: "#fff8ee",
        colorBgSecondary: "#eef4ff",
        colorSurface: "#ffffff",
        colorHeader: "#2c479c",
        colorHeaderGradient: "linear-gradient(135deg, #2c479c 0%, #17326f 100%)",
        colorPrimary: "#dd0425",
        colorPrimaryDark: "#b4031d",
        colorPrimaryGradient: "linear-gradient(135deg, #dd0425 0%, #f4ba36 100%)",
        colorSecondary: "#f4ba36",
        colorSecondaryLight: "#fff0c7",
        colorSuccess: "#7bc624",
        colorSuccessDark: "#5c9e14",
        colorText: "#24324a",
        colorTextSecondary: "#5f6f8b",
        colorTextLight: "#8ea0bb",
        colorBorder: "#d9e2f0",
        colorBorderLight: "#edf2f8",
        colorDanger: "#dd0425",
        colorOverlay: "rgba(17, 36, 79, 0.62)",
    },

    kokoriko: {
        colorBg: "#f5f0eb",
        colorBgSecondary: "#ede5dc",
        colorSurface: "#ffffff",
        colorHeader: "#1a1a2e",
        colorHeaderGradient: "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
        colorPrimary: "#e63946",
        colorPrimaryDark: "#c1121f",
        colorPrimaryGradient: "linear-gradient(135deg, #e63946 0%, #f4a261 100%)",
        colorSecondary: "#f4a261",
        colorSecondaryLight: "#fce4b8",
        colorSuccess: "#25d366",
        colorSuccessDark: "#1da851",
        colorText: "#2d2d2d",
        colorTextSecondary: "#6b7280",
        colorTextLight: "#9ca3af",
        colorBorder: "#e5e7eb",
        colorBorderLight: "#f3f4f6",
        colorDanger: "#ef4444",
        colorOverlay: "rgba(0, 0, 0, 0.6)",
    },

    elegant: {
        colorBg: "#fafafa",
        colorBgSecondary: "#f0f0f0",
        colorSurface: "#ffffff",
        colorHeader: "#2c2c2c",
        colorHeaderGradient: "linear-gradient(135deg, #2c2c2c 0%, #434343 100%)",
        colorPrimary: "#c9a96e",
        colorPrimaryDark: "#b08d4f",
        colorPrimaryGradient: "linear-gradient(135deg, #c9a96e 0%, #d4af37 100%)",
        colorSecondary: "#d4af37",
        colorSecondaryLight: "#f5e6b8",
        colorSuccess: "#25d366",
        colorSuccessDark: "#1da851",
        colorText: "#1a1a1a",
        colorTextSecondary: "#555555",
        colorTextLight: "#999999",
        colorBorder: "#e0e0e0",
        colorBorderLight: "#f0f0f0",
        colorDanger: "#d32f2f",
        colorOverlay: "rgba(0, 0, 0, 0.55)",
    },

    ocean: {
        colorBg: "#f0f4f8",
        colorBgSecondary: "#e2e8f0",
        colorSurface: "#ffffff",
        colorHeader: "#0f2b46",
        colorHeaderGradient: "linear-gradient(135deg, #0f2b46 0%, #1a3a5c 100%)",
        colorPrimary: "#0077b6",
        colorPrimaryDark: "#005f8d",
        colorPrimaryGradient: "linear-gradient(135deg, #0077b6 0%, #00b4d8 100%)",
        colorSecondary: "#00b4d8",
        colorSecondaryLight: "#caf0f8",
        colorSuccess: "#25d366",
        colorSuccessDark: "#1da851",
        colorText: "#1e293b",
        colorTextSecondary: "#475569",
        colorTextLight: "#94a3b8",
        colorBorder: "#cbd5e1",
        colorBorderLight: "#e2e8f0",
        colorDanger: "#ef4444",
        colorOverlay: "rgba(0, 0, 0, 0.55)",
    },

    dark: {
        colorBg: "#121212",
        colorBgSecondary: "#1e1e1e",
        colorSurface: "#2a2a2a",
        colorHeader: "#0a0a0a",
        colorHeaderGradient: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
        colorPrimary: "#ff6b6b",
        colorPrimaryDark: "#ee5a5a",
        colorPrimaryGradient: "linear-gradient(135deg, #ff6b6b 0%, #ffa06b 100%)",
        colorSecondary: "#ffa06b",
        colorSecondaryLight: "#3d2e1e",
        colorSuccess: "#25d366",
        colorSuccessDark: "#1da851",
        colorText: "#e0e0e0",
        colorTextSecondary: "#a0a0a0",
        colorTextLight: "#707070",
        colorBorder: "#3a3a3a",
        colorBorderLight: "#2e2e2e",
        colorDanger: "#ff5252",
        colorOverlay: "rgba(0, 0, 0, 0.75)",
    },
};

// ===== CSS Variable Mapping =====
// Maps JS property names → CSS custom property names
const cssVarMap = {
    colorBg: "--color-bg",
    colorBgSecondary: "--color-bg-secondary",
    colorSurface: "--color-surface",
    colorHeader: "--color-header",
    colorHeaderGradient: "--color-header-gradient",
    colorPrimary: "--color-primary",
    colorPrimaryDark: "--color-primary-dark",
    colorPrimaryGradient: "--color-primary-gradient",
    colorSecondary: "--color-secondary",
    colorSecondaryLight: "--color-secondary-light",
    colorSuccess: "--color-success",
    colorSuccessDark: "--color-success-dark",
    colorText: "--color-text",
    colorTextSecondary: "--color-text-secondary",
    colorTextLight: "--color-text-light",
    colorBorder: "--color-border",
    colorBorderLight: "--color-border-light",
    colorDanger: "--color-danger",
    colorOverlay: "--color-overlay",
};

// ===== ENV Override Mapping =====
// Maps REACT_APP_COLOR_* env vars → JS property names
const envOverrides = {
    REACT_APP_COLOR_PRIMARY: "colorPrimary",
    REACT_APP_COLOR_PRIMARY_DARK: "colorPrimaryDark",
    REACT_APP_COLOR_SECONDARY: "colorSecondary",
    REACT_APP_COLOR_HEADER: "colorHeader",
    REACT_APP_COLOR_BG: "colorBg",
    REACT_APP_COLOR_SURFACE: "colorSurface",
    REACT_APP_COLOR_TEXT: "colorText",
    REACT_APP_COLOR_SUCCESS: "colorSuccess",
    REACT_APP_COLOR_DANGER: "colorDanger",
};

/**
 * Applies the selected theme to the document.
 * Reads REACT_APP_THEME from .env for base theme,
 * then applies any REACT_APP_COLOR_* overrides.
 */
export function applyTheme() {
    const themeName = process.env.REACT_APP_THEME || "kokoriko";
    const baseTheme = themes[themeName] || themes.kokoriko;

    // Create a merged theme with env overrides
    const mergedTheme = { ...baseTheme };

    // Apply individual color overrides from .env
    Object.entries(envOverrides).forEach(([envVar, propName]) => {
        const value = process.env[envVar];
        if (value) {
            mergedTheme[propName] = value;

            // Auto-generate gradient if primary color is overridden
            if (propName === "colorPrimary" && !process.env.REACT_APP_COLOR_PRIMARY_GRADIENT) {
                mergedTheme.colorPrimaryGradient = `linear-gradient(135deg, ${value} 0%, ${mergedTheme.colorSecondary} 100%)`;
            }
        }
    });

    // Handle explicit gradient override
    if (process.env.REACT_APP_COLOR_PRIMARY_GRADIENT) {
        mergedTheme.colorPrimaryGradient = process.env.REACT_APP_COLOR_PRIMARY_GRADIENT;
    }

    // Handle explicit header gradient override
    if (process.env.REACT_APP_COLOR_HEADER_GRADIENT) {
        mergedTheme.colorHeaderGradient = process.env.REACT_APP_COLOR_HEADER_GRADIENT;
    }

    // Apply all theme values as CSS custom properties
    const root = document.documentElement;
    Object.entries(cssVarMap).forEach(([prop, cssVar]) => {
        if (mergedTheme[prop]) {
            root.style.setProperty(cssVar, mergedTheme[prop]);
        }
    });

    // Log applied theme in dev mode
    if (process.env.REACT_APP_DEV_MODE === "true") {
        console.log(`🎨 Theme applied: "${themeName}"`, mergedTheme);
    }
}

/**
 * Returns the current computed value of a CSS custom property.
 * Useful for JS contexts that can't use var() (e.g. SweetAlert2).
 */
export function getThemeColor(cssVarName) {
    return getComputedStyle(document.documentElement)
        .getPropertyValue(cssVarName)
        .trim();
}

/**
 * Returns the header image path from .env or fallback.
 */
export function getHeaderImage() {
    return process.env.REACT_APP_HEADER_IMAGE || "/img/BannerSurtifamiliar.png";
}

/**
 * Returns all available theme names.
 */
export function getAvailableThemes() {
    return Object.keys(themes);
}

export default themes;
